if (!customElements.get('ufm-optional-extras')) {
  customElements.define('ufm-optional-extras', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = this.closest('form');
      if (!this.form) return;
      this.variants = JSON.parse(this.querySelector('[data-main-variants]').textContent);
      this.button = this.form.querySelector('[data-ufm-add-button]');
      this.rows = [...this.querySelectorAll('[data-extra-row]')];
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: this.dataset.currency
      });
      this.form.addEventListener('change', () => this.update());
      this.form.addEventListener('input', () => this.update());
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.initialized = true;
      this.hidden = false;
      this.update();
    }

    mainVariant() {
      return this.variants.find((variant) => String(variant.id) === this.form.elements.id.value);
    }

    extraData(row) {
      const input = row.querySelector('[data-extra-variant]');
      const choice = input.tagName === 'SELECT' ? input.selectedOptions[0] : input;
      return {
        id: input.value,
        price: Number(choice.dataset.price || 0),
        quantity: Math.max(1, Number(choice.dataset.quantity || 1)),
        money: choice.dataset.money
      };
    }

    update() {
      let extras = 0;
      let pending = false;
      this.rows.forEach((row) => {
        const checked = row.querySelector('[data-extra-checkbox]').checked;
        const input = row.querySelector('[data-extra-variant]');
        if (input.tagName === 'SELECT') {
          input.disabled = !checked || this.busy;
        }
        row.classList.toggle('is-selected', checked);
        const data = this.extraData(row);
        if (data.money) row.querySelector('[data-extra-price]').textContent = data.money;
        const quantityNote = row.querySelector('[data-extra-quantity]');
        quantityNote.hidden = data.quantity === 1;
        quantityNote.textContent = `Minimum quantity: ${data.quantity} per selected extra`;
        if (checked) {
          extras += data.price * data.quantity;
          if (!data.id) pending = true;
        }
      });
      const main = (this.mainVariant()?.price || 0) * Number(this.form.elements.quantity.value || 1);
      this.querySelector('[data-main-total]').textContent = this.money.format(main / 100);
      this.querySelector('[data-extras-total]').textContent = this.money.format(extras / 100);
      this.querySelector('[data-combined-total]').textContent = this.money.format((main + extras) / 100);
      this.querySelector('[data-extra-pending]').hidden = !pending;
      if (this.busy) this.button.disabled = true;
    }

    async submit(event) {
      if (event.submitter && event.submitter !== this.button) return;
      event.preventDefault();
      if (this.busy || !this.mainVariant()?.available || !this.form.reportValidity()) return;
      const quantityInput = this.form.elements.quantity;
      if (!quantityInput.value || Number(quantityInput.value) <= 0) {
        quantityInput.setCustomValidity('Enter a quantity greater than zero.');
        quantityInput.reportValidity();
        quantityInput.setCustomValidity('');
        return;
      }
      for (const row of this.rows) {
        const input = row.querySelector('[data-extra-variant]');
        if (row.querySelector('[data-extra-checkbox]').checked && !input.value) {
          input.setCustomValidity('Choose a variant for this optional extra.');
          input.reportValidity();
          input.setCustomValidity('');
          return;
        }
      }
      const items = [{ id: this.form.elements.id.value, quantity: Number(quantityInput.value) }];
      // Preserve any existing main-line properties and selling plan, not add-on pricing.
      const fields = new FormData(this.form);
      const properties = {};
      for (const [name, value] of fields) {
        const match = name.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      }
      if (Object.keys(properties).length) items[0].properties = properties;
      if (fields.get('selling_plan')) items[0].selling_plan = fields.get('selling_plan');
      this.rows.forEach((row) => {
        const checkbox = row.querySelector('[data-extra-checkbox]');
        if (checkbox.checked && !checkbox.disabled) {
          const data = this.extraData(row);
          items.push({ id: data.id, quantity: data.quantity });
        }
      });
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : [];
      const status = this.querySelector('[data-extra-status]');
      const cartLink = this.querySelector('[data-extra-cart-link]');
      status.hidden = true;
      cartLink.hidden = true;
      this.busy = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.update();
      let added = false;
      try {
        cart?.setActiveElement(this.button);
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, ...(sections.length ? { sections, sections_url: window.location.pathname } : {}) })
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || 'Unable to add these items. Please check availability and try again.');
        added = true;
        if (cart) {
          // Notification expects one line key; multi-add returns an items array.
          const mainItem = state.items?.[0];
          const renderState = { ...state, key: mainItem?.key, id: mainItem?.id };
          if (sections.some((id) => !state.sections?.[id])) throw new Error('Cart sections unavailable');
          cart.classList.remove('is-empty');
          cart.renderContents(renderState);
          status.textContent = 'Selected items added to your basket.';
          status.hidden = false;
        } else {
          // The existing main form navigates to the cart when no AJAX cart is present.
          window.location.assign(window.routes?.cart_url || cartLink.href);
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ufm-optional-extras', productVariantId: items[0].id, cartData: state });
        }
      } catch (error) {
        status.textContent = added
          ? 'Your items were added, but the basket preview could not refresh. View your basket before adding again.'
          : error.message;
        status.hidden = false;
        cartLink.hidden = !added;
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
        this.button.disabled = !this.mainVariant()?.available;
        this.update();
      }
    }
  });
}
